import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { ArrowLeft, Send, Printer } from "lucide-react";
import { toast } from "sonner";
import PrintReceipt from "../components/PrintReceipt";

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [branch, setBranch] = useState(null);
  const [showPrint, setShowPrint] = useState(false);

  useEffect(() => {
    api.get(`/invoices/${id}`).then(r => setInv(r.data));
    api.get("/branches").then(r => setBranch(r.data?.[0])).catch(() => {});
  }, [id]);

  const sendWa = async () => {
    try { await api.post(`/invoices/${id}/send-whatsapp`); toast.success("Invoice sent via WhatsApp"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const print = () => {
    setShowPrint(true);
    setTimeout(() => { window.print(); setTimeout(() => setShowPrint(false), 800); }, 200);
  };

  if (!inv) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="invoice-detail">
      <Button asChild variant="ghost" size="sm"><Link to="/invoices"><ArrowLeft className="w-4 h-4 mr-1" />All invoices</Link></Button>
      <Card className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Tax Invoice</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight mt-1">{inv.invoice_no}</h2>
            <div className="text-sm text-muted-foreground mt-1">{new Date(inv.created_at).toLocaleString()}</div>
            {inv.customer && (
              <div className="mt-3 text-sm">
                <div className="font-medium">{inv.customer.name}</div>
                <div className="font-mono text-xs">{inv.customer.mobile_masked || inv.customer.mobile}</div>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="font-heading text-4xl font-black tracking-tighter">₹{inv.grand_total.toFixed(2)}</div>
            <Badge variant={inv.status === "paid" ? "secondary" : "destructive"} className="mt-1">{inv.status}</Badge>
            <div className="flex gap-2 mt-3 justify-end flex-wrap">
              <Button onClick={print} variant="outline" data-testid="print-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
              {inv.customer_id && (
                <Button onClick={sendWa} data-testid="send-wa-btn"><Send className="w-4 h-4 mr-1" />Send WhatsApp</Button>
              )}
            </div>
          </div>
        </div>

        <Table className="mt-6">
          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>HSN</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Disc%</TableHead><TableHead className="text-right">GST%</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
          <TableBody>
            {inv.items.map((it, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="font-medium text-sm">{it.name}</div>
                  <div className="text-xs text-muted-foreground uppercase">{it.kind}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{it.hsn_code || "—"}</TableCell>
                <TableCell className="text-right">{it.qty}</TableCell>
                <TableCell className="text-right font-mono">₹{it.unit_price.toFixed(2)}</TableCell>
                <TableCell className="text-right">{it.discount_percent}%</TableCell>
                <TableCell className="text-right">{it.gst_percent}%</TableCell>
                <TableCell className="text-right font-mono">₹{it.final_total.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <Row label="Subtotal" v={`₹${inv.subtotal.toFixed(2)}`} />
            <Row label="Tax" v={`₹${inv.tax_amount.toFixed(2)}`} />
            {inv.discount_amount > 0 && <Row label="Bill discount" v={`-₹${inv.discount_amount.toFixed(2)}`} />}
            <div className="h-px bg-border my-2" />
            <Row bold label="Grand total" v={`₹${inv.grand_total.toFixed(2)}`} />
            <Row label="Paid" v={`₹${inv.paid_total.toFixed(2)}`} />
            {inv.balance > 0 && <Row label="Balance" v={`₹${inv.balance.toFixed(2)}`} />}
          </div>
        </div>

        {inv.payments?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Payments</div>
            <div className="flex flex-wrap gap-2">
              {inv.payments.map((p, i) => (
                <div key={i} className="px-3 py-2 rounded-md border border-border text-sm">
                  <span className="uppercase text-xs text-muted-foreground">{p.method}</span> · <span className="font-mono">₹{p.amount.toFixed(2)}</span>
                  {p.reference && <span className="text-xs text-muted-foreground ml-2">{p.reference}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
      {showPrint && <PrintReceipt invoice={inv} branch={branch} />}
    </div>
  );
}

const Row = ({ label, v, bold }) => (
  <div className="flex items-center justify-between">
    <span className={bold ? "font-semibold" : "text-muted-foreground"}>{label}</span>
    <span className={`font-mono ${bold ? "font-bold text-lg" : ""}`}>{v}</span>
  </div>
);
